// src/components/booking/AccommodationPlaceCard.jsx
import React, { useEffect, useState } from 'react';
import { 
  Star, 
  MapPin, 
  Heart,
  Eye,
  Calendar,
  MessageCircle,
  User,
  Users,
  Bed,
  Wifi,
  Car,
  Coffee,
  Waves,
  Dumbbell,
  ParkingCircle,
  UtensilsCrossed,
  Shield,
  Award,
  Clock,
  CheckCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { ImageModal } from '../common/ImageModal';
import { BookingModal } from './BookingModal';

// Pricing type configurations
const PRICING_TYPES = {
  'per_person': {
    icon: User,
    label: 'per person',
    color: '#3b82f6'
  },
  'per_two_people': {
    icon: Users,
    label: 'per 2 people', 
    color: '#10b981'
  },
  'per_room': {
    icon: Bed,
    label: 'per room',
    color: '#8b5cf6'
  },
  'per_night': {
    icon: Calendar,
    label: 'per night',
    color: '#f59e0b'
  }
};

// Popular amenities with icons
const AMENITY_ICONS = {
  wifi: Wifi,
  parking: ParkingCircle,
  breakfast: Coffee,
  pool: Waves,
  gym: Dumbbell,
  restaurant: UtensilsCrossed,
  spa: Award,
  security: Shield,
  ac: Clock // Using clock as placeholder for AC
};

export const AccommodationPlaceCard = ({ 
  place, 
  customStyling, 
  onViewDetails, 
  bookSettings,
  searchCriteria,
  onBookPlace, 
  onInquire,
  className = ''
}) => {
  const [isFavorited, setIsFavorited] = useState(place.isFavorited || false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);

  const [showBookingModal, setShowBookingModal] = useState(false);

  const images = place.images || [];
  const hasImages = images.length > 0;
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // Determine pricing configuration
  const pricingType = place.pricingType || 'per_room';
  const pricing = PRICING_TYPES[pricingType];
  const PriceIcon = pricing?.icon || Bed;

  // Get primary image with fallback
  const primaryImage = place.images?.[0]?.url || place.imageUrl || place.thumbnail;
  const displayImage = hasImages
    ? (images[currentImageIndex]?.url || primaryImage)
    : primaryImage;
  
  // Extract key amenities with icons (max 4)
  const featuredAmenities = (place.amenities || [])
    .filter(amenity => AMENITY_ICONS[amenity.toLowerCase()])
    .slice(0, 4);

  // Handle favorite toggle
  const handleFavoriteToggle = (e) => {
    e.stopPropagation();
    setIsFavorited(!isFavorited);
    // Could trigger API call here to save favorite status
  };

  // Action handlers
  const handleViewDetails = (e) => {
    e.stopPropagation();
    onViewDetails?.(place);
  };

  const handleBookPlace = (e) => {
    e.stopPropagation();
    setShowBookingModal(true);
    // onBookPlace?.(place); // Keep this if you want the parent to also handle the event
  };
  const handleInquire = (e) => {
    e.stopPropagation();
    onInquire?.(place);
  };

  const handleImageClick = (e, index = 0) => {
    e.stopPropagation();
    if (hasImages) {
      setModalImageIndex(index);
      setShowImageModal(true);
    }
  };

  const handleEyeClick = (e) => {
    e.stopPropagation();
    if (hasImages) {
      setModalImageIndex(0);
      setShowImageModal(true);
    }
  };

  useEffect(() => {
    setCurrentImageIndex(0);
    setImageLoaded(false);
  }, [place.id, images.length]);

  const handlePrevImage = (e) => {
    e.stopPropagation();
    if (images.length < 2) return;
    setImageLoaded(false);
    setCurrentImageIndex(prev => (prev - 1 + images.length) % images.length);
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    if (images.length < 2) return;
    setImageLoaded(false);
    setCurrentImageIndex(prev => (prev + 1) % images.length);
  };
  
  if (showBookingModal) {
    return (
      <BookingModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        place={place}
        searchCriteria={searchCriteria} // Pass this from parent
        bookSettings={bookSettings} // Pass this from parent
        customStyling={customStyling}
      />
    );
  }

  return (
    <div 
      className={`bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 group cursor-pointer ${className}`}
    >
      {/* Image Section */}
      <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden">
        {displayImage ? (
          <>
            <img
              src={displayImage}
              alt={place.title}
              className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={() => setImageLoaded(true)}
              onClick={(e) => handleImageClick(e, currentImageIndex)}
            />
            
            {!imageLoaded && (
              <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
                <Bed className="h-8 w-8 text-gray-400" />
              </div>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
            <Bed className="h-12 w-12 text-gray-400" />
          </div>
        )}

        {/* Top Overlay - Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          {place.isVerified && (
            <div className="bg-emerald-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
              <CheckCircle className="h-3 w-3" />
              Verified
            </div>
          )}
          {place.isFeatured && (
            <div 
              className="text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1"
              style={{ backgroundColor: customStyling?.accentColor || '#f59e0b' }}
            >
              <Award className="h-3 w-3" />
              Featured
            </div>
          )}
        </div>

        {/* Favorite Button */}
        <button
          onClick={handleFavoriteToggle}
          className={`absolute top-3 right-3 p-2 rounded-full backdrop-blur-md transition-all duration-200 ${
            isFavorited 
              ? 'bg-red-500 text-white shadow-lg' 
              : 'bg-white/80 text-gray-600 hover:bg-white hover:text-red-500'
          }`}
        >
          <Heart className={`h-4 w-4 ${isFavorited ? 'fill-current' : ''}`} />
        </button>

        {/* Image Navigation */}
        {place.images && place.images.length > 1 && (
          <>
            <button
              onClick={handlePrevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 text-gray-700 hover:bg-white shadow-sm"
              title="Previous image"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={handleNextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 text-gray-700 hover:bg-white shadow-sm"
              title="Next image"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <div className="absolute bottom-3 right-3 bg-black/60 text-white px-2 py-1 rounded-full text-xs backdrop-blur-sm">
              {currentImageIndex + 1} / {place.images.length}
            </div>
          </>
        )}

        {/* Price Badge */}
        <div className="absolute bottom-3 left-3">
          <div className="bg-white/60 backdrop-blur-sm px-3 py-2 rounded-xl shadow-lg">
            <div className="flex items-center gap-2">
              <PriceIcon className="h-4 w-4 text-gray-600" />
              <div>
                <span className="font-bold text-[#ff5757] text-lg">
                  R{place.price?.toLocaleString() || '0'}
                </span>
                <div className="text-xs text-gray-600 leading-none">
                  {pricing?.label || 'per night'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="py-3 px-4 mt-2">
        {/* Header */}
        <div className="mb-3">
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-bold text-gray-900 text-lg line-clamp-1 pr-2">
              {place.title}
            </h3>
            {place.rating && (
              <div className="flex items-center gap-1 flex-shrink-0">
                <Star className="h-4 w-4 text-yellow-400 fill-current" />
                <span className="font-semibold text-gray-900 text-sm">
                  {place.rating}
                </span>
                {place.reviewCount && (
                  <span className="text-gray-500 text-xs">
                    ({place.reviewCount})
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Location */}
          <div className="flex items-center gap-1 text-gray-600">
            <MapPin className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm line-clamp-1">{place.location}</span>
          </div>
        </div>

        {/* Description */}
        <p className="text-gray-600 text-sm line-clamp-2 mb-4 leading-relaxed">
          {place.description}
        </p>

        {/* Featured Amenities */}
        {featuredAmenities.length > 0 && (
          <div className="flex items-center gap-3 mb-2">
            {featuredAmenities.map((amenity, index) => {
              const AmenityIcon = AMENITY_ICONS[amenity.toLowerCase()];
              return AmenityIcon ? (
                <div
                  key={index}
                  className="flex items-center gap-1 text-gray-600 text-xs"
                  title={amenity.charAt(0).toUpperCase() + amenity.slice(1)}
                >
                  <AmenityIcon className="h-4 w-4" />
                </div>
              ) : null;
            })}
            {place.amenities && place.amenities.length > 4 && (
              <span className="text-xs text-gray-500">
                +{place.amenities.length - 4} more
              </span>
            )}
          </div>
        )}

        {/* Availability Status */}
        {place.availability && (
          <div className="mb-2">
            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              place.availability === 'available' 
                ? 'bg-emerald-100 text-emerald-700'
                : place.availability === 'limited'
                ? 'bg-yellow-100 text-yellow-700' 
                : 'bg-red-100 text-red-700'
            }`}>
              <div className={`w-2 h-2 rounded-full ${
                place.availability === 'available' 
                  ? 'bg-emerald-500'
                  : place.availability === 'limited'
                  ? 'bg-yellow-500'
                  : 'bg-red-500'
              }`} />
              {place.availability === 'available' && 'Available'}
              {place.availability === 'limited' && 'Limited availability'}
              {place.availability === 'unavailable' && 'Fully booked'}
            </div>
          </div>
        )}

        {/* Action Icons */}
        <div className="flex items-center justify-end gap-3">
          {/* View Details */}
          <button
            onClick={handleEyeClick}
            className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors group/btn"
            title="View Details"
          >
            <Eye className="h-4 w-4 group-hover/btn:scale-110 transition-transform" />
          </button>

          {/* Inquire */}
          <button
            onClick={handleInquire}
            className="p-2 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors group/btn"
            title="Send Inquiry"
          >
            <MessageCircle className="h-4 w-4 group-hover/btn:scale-110 transition-transform" />
          </button>

          {/* Book Now */}
          <button
            onClick={handleBookPlace}
            className="p-2 rounded-full text-white hover:opacity-90 transition-all group/btn"
            style={{ backgroundColor: customStyling?.primaryColor || '#10b981' }}
            title="Book Now"
          >
            <Calendar className="h-4 w-4 group-hover/btn:scale-110 transition-transform" />
          </button>
        </div>
      </div>

      <ImageModal
      isOpen={showImageModal}
      onClose={() => setShowImageModal(false)}
      images={images}
      initialIndex={modalImageIndex}
      title={place.title}
      description={place.description}
      allowDownload={false}
      customStyling={customStyling}
    />

    </div>
  );
};

export default AccommodationPlaceCard;
